/**
 * followup-api.ts — Quy tắc "Chủ động theo dõi khách hàng" của nhân viên AI
 * (backend: modules/ai/followup/*). KHÔNG phiên bản hoá — lưu là có hiệu lực ngay.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import type { Condition } from './bot-settings'
import { i18n } from '@/lib/i18n'

export type FollowupTrigger = 'after_ai_reply' | 'after_customer_msg'
export type FollowupAudience = { mode: 'all' } | { mode: 'conditions'; conditions: Condition[] }
export interface FollowupRule {
  id: string
  botId: string
  name: string
  trigger: FollowupTrigger
  delayMinutes: number
  instruction: string
  audience: FollowupAudience
  enabled: boolean
  sort: number
  createdAt: string
  updatedAt: string
  firedCount: number
}
export type FollowupRuleInput = Omit<FollowupRule, 'id' | 'botId' | 'sort' | 'createdAt' | 'updatedAt' | 'firedCount'>

export const FOLLOWUP_TRIGGER_OPTIONS: Array<{ value: FollowupTrigger; label: string }> = [
  { value: 'after_ai_reply', get label() { return i18n.t('aiTrainLib:followupApi.trigger.afterAiReply') } },
  { value: 'after_customer_msg', get label() { return i18n.t('aiTrainLib:followupApi.trigger.afterCustomerMsg') } },
]
export type DelayUnit = 'minutes' | 'hours' | 'days'
export const DELAY_UNITS: Array<{ value: DelayUnit; label: string; minutes: number }> = [
  { value: 'minutes', get label() { return i18n.t('aiTrainLib:followupApi.unit.minutes') }, minutes: 1 },
  { value: 'hours', get label() { return i18n.t('aiTrainLib:followupApi.unit.hours') }, minutes: 60 },
  { value: 'days', get label() { return i18n.t('aiTrainLib:followupApi.unit.days') }, minutes: 1440 },
]

/** Tách số phút thành (số, đơn vị) gọn nhất. */
export function splitDelay(minutes: number): { amount: number; unit: DelayUnit } {
  if (minutes > 0 && minutes % 1440 === 0) return { amount: minutes / 1440, unit: 'days' }
  if (minutes > 0 && minutes % 60 === 0) return { amount: minutes / 60, unit: 'hours' }
  return { amount: minutes, unit: 'minutes' }
}
export function joinDelay(amount: number, unit: DelayUnit): number {
  return Math.round(amount) * (DELAY_UNITS.find((u) => u.value === unit)?.minutes ?? 1)
}
/** "10 phút sau phản hồi cuối của AI" */
export function describeDelay(minutes: number, trigger: FollowupTrigger): string {
  const { amount, unit } = splitDelay(minutes)
  const u = DELAY_UNITS.find((x) => x.value === unit)?.label ?? i18n.t('aiTrainLib:followupApi.unit.minutes')
  return i18n.t('aiTrainLib:followupApi.describeDelay', { amount, unit: u, event: trigger === 'after_ai_reply' ? i18n.t('aiTrainLib:followupApi.event.aiReply') : i18n.t('aiTrainLib:followupApi.event.customerMsg') })
}

export const followupKeys = { list: (botId: string) => ['ai', 'train', 'followup', botId] as unknown[] }

export function useFollowupRules(botId: string | undefined) {
  return useQuery<{ rules: FollowupRule[]; defaultName: string }>({
    queryKey: followupKeys.list(botId ?? ''),
    enabled: !!botId,
    queryFn: async () => (await api.get(`/ai/bots/${botId}/followup-rules`)).data,
  })
}
export async function createFollowupRule(botId: string, input: FollowupRuleInput) {
  return (await api.post<{ rule: FollowupRule }>(`/ai/bots/${botId}/followup-rules`, input)).data.rule
}
export async function updateFollowupRule(botId: string, ruleId: string, input: Partial<FollowupRuleInput>) {
  return (await api.put<{ rule: FollowupRule }>(`/ai/bots/${botId}/followup-rules/${ruleId}`, input)).data.rule
}
export async function deleteFollowupRule(botId: string, ruleId: string) {
  await api.delete(`/ai/bots/${botId}/followup-rules/${ruleId}`)
}

/** Ví dụ hướng dẫn theo dõi (tiếng Việt) — chèn vào ô hướng dẫn. Tiêu đề: dịch; `text` là chỉ dẫn lưu cho AI (dữ liệu) → giữ nguyên. */
export const FOLLOWUP_EXAMPLES: Array<{ title: string; text: string }> = [
  {
    get title() { return i18n.t('aiTrainLib:followupApi.example.cart') },
    text: 'Nhắc nhẹ khách về sản phẩm vừa tư vấn, hỏi khách còn phân vân điểm nào (giá, size, màu, thời gian giao) để em hỗ trợ thêm. Không giục mua, không nhắc giảm giá nếu chưa có.', // i18n-ignore (hướng dẫn gửi cho AI)
  },
  {
    get title() { return i18n.t('aiTrainLib:followupApi.example.question') },
    text: 'Hỏi thăm khách đã rõ thông tin chưa, còn điều gì cần em giải thích thêm không. Gợi ý một câu hỏi khách thường quan tâm tiếp theo dựa trên nội dung đã trao đổi.', // i18n-ignore (hướng dẫn gửi cho AI)
  },
  {
    get title() { return i18n.t('aiTrainLib:followupApi.example.offer') },
    text: 'Nhắc khách ưu đãi đã trao đổi trong hội thoại sắp kết thúc (chỉ nhắc nếu trong hội thoại có nói về ưu đãi), hỏi khách có muốn em giữ đơn / lên đơn giúp không.', // i18n-ignore (hướng dẫn gửi cho AI)
  },
]
